import { AxiosError } from 'axios';
import type { ApiErrorResponse, ApiValidationErrorDetail } from '@dopomogai/types';

/**
 * Non-throwing API error handler.
 * Returns a standardized ApiErrorResponse for all failed requests.
 */
export const handleApiError = (error: unknown, operation: string): ApiErrorResponse => {
  const axiosError = error as AxiosError;

  // Keep the existing logging behavior to aid debugging.
  console.error(
    `${axiosError.config?.method?.toUpperCase()} ${axiosError.config?.url} - Error ${operation}:`,
    error
  );

  let message = 'An unknown error occurred.';
  const status = axiosError.response?.status ?? null;
  const errorData = axiosError.response?.data;

  if (axiosError.response?.data) {
    const data = axiosError.response.data as any;

    if (typeof data.detail === 'string') {
      message = data.detail;
    } else if (
      Array.isArray(data.detail) &&
      data.detail.every((entry: any) => typeof entry.loc !== 'undefined' && typeof entry.msg !== 'undefined')
    ) {
      message = data.detail
        .map((entry: ApiValidationErrorDetail) => {
          const loc = entry.loc?.slice(1).join('.') || 'field';
          return `${loc}: ${entry.msg}`;
        })
        .join('; ');

      if (!message && data.detail.length > 0) message = 'Validation error with multiple fields.';
      else if (!message) message = 'Validation error.';
    } else if (typeof data === 'string' && data.length < 500 && !data.trim().startsWith('<')) {
      message = data;
    } else if (data.message && typeof data.message === 'string') {
      message = data.message;
    } else if (data.error && typeof data.error === 'string') {
      message = data.error;
    } else if (status) {
      message = `Request failed with status code ${status}`;
    }

    if (typeof message === 'string' && message.trim().startsWith('<')) {
      message = `Received an HTML error page (status ${status || 'unknown'}) during ${operation}.`;
    }
  } else if ((axiosError as any).request) {
    message = `No response from server during ${operation}. Check network connection.`;
  } else if ((error as any)?.message) {
    message = (error as any).message;
  }

  return {
    isError: true,
    message,
    status,
    data: errorData,
    originalError: error,
  };
};
